import React, { useState, useEffect, useRef } from 'react';
import { tradingAgentApi } from '../services/api';
import api from '../services/api';
import { walletApi } from '../services/walletApi';
import WalletSelector from '../components/WalletSelector';
import { formatAgentName, formatWalletName } from '../utils/formatNames';
import WalletGroupSelector from './WalletGroupSelector';
import globalWebSocketService from '../services/globalWebSocketService';
import { useToken } from '../contexts/TokenContext';

const AgentChat = (props) => {
    const { agent, onStrategyStart, onWalletSelect } = props;
    const { tokenId, tokenData } = useToken();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  
  // Debug messages state changes  
  useEffect(() => {
    console.log(`[MESSAGES STATE] Messages changed: ${messages.length} messages for agent ${agent?.id}${tokenId ? ` with token ${tokenId}` : ''}`);
    if (messages.length > 0) {
      console.log(`[MESSAGES STATE] First 3:`, messages.slice(0, 3).map(m => ({ role: m.role, content: m.content?.substring(0, 30) + '...' })));
    } else {
      console.log(`[MESSAGES STATE] No messages displayed`);
    }
  }, [messages.length, agent?.id, tokenId]);
  
  const [loading, setLoading] = useState(false);
  const [parsedStrategy, setParsedStrategy] = useState(null);
  const [tokenAddress, setTokenAddress] = useState('');
  const [walletAddresses, setWalletAddresses] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [usingLocalStorage, setUsingLocalStorage] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [generatingWallet, setGeneratingWallet] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const previousAgentIdRef = useRef(null);
  const messageEndRef = useRef(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupWallets, setGroupWallets] = useState([]);
  const wsRef = useRef(null);
  const [waitingForAnswer, setWaitingForAnswer] = useState(false);
  const [waitingForStrategy, setWaitingForStrategy] = useState(false);
  
  // Message ID generator to avoid duplicates
  const messageIdRef = useRef(0);
  const generateUniqueId = () => {
    messageIdRef.current += 1;
    return `${Date.now()}-${messageIdRef.current}`;
  };
  
  // Track processed WebSocket messages to prevent duplicates
  const processedMessages = useRef(new Set());
  
  // Track the current agent external_id to prevent cross-contamination
  const currentAgentExternalIdRef = useRef(null);

    useEffect(() => {
  if (selectedWallet && onWalletSelect) {
    onWalletSelect(selectedWallet);
  }
}, [selectedWallet, onWalletSelect]);
  
  // WebSocket connection for real-time chat updates
  useEffect(() => {
    if (!agent || !agent.external_id) return;
    
    // Update the current agent ref for safety checks
    currentAgentExternalIdRef.current = agent.external_id;
    
    // Clear processed messages when switching agents
    processedMessages.current.clear();
    console.log(`AgentChat: Cleared processed messages for new agent ${agent.external_id}`);

    const connectWebSocket = () => {
      // Use wss:// for production Heroku app  
      const wsUrl = `wss://token-ai-agent-d09010489214.herokuapp.com/ws/agent_chat/${agent.external_id}`;
      console.log(`AgentChat: Connecting to WebSocket at ${wsUrl} for agent ${agent.id} (external: ${agent.external_id})`);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log(`AgentChat: WebSocket connected for agent ${agent.id} (external: ${agent.external_id})`);
        // Enable global WebSocket service for message persistence
        // This ensures messages are saved even when user switches agents
        globalWebSocketService.startConnection(agent.id, agent.external_id, null, 'chat');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`AgentChat: Received WebSocket message for agent check:`, {
            messageAgentId: data.agent_id,
            currentAgentExternalId: agent.external_id,
            messageContent: data.message?.substring(0, 50) + '...'
          });
          
          // CRITICAL: Check if this component is still mounted for the same agent
          // This prevents state updates when user has switched to a different agent
          if (!agent || !agent.external_id || !currentAgentExternalIdRef.current) {
            console.log(`AgentChat: BLOCKED - No current agent available`);
            return;
          }
          
          // Double-check against both the prop and the ref to prevent race conditions
          if (currentAgentExternalIdRef.current !== agent.external_id) {
            console.log(`AgentChat: BLOCKED - Agent ref mismatch: ref=${currentAgentExternalIdRef.current}, prop=${agent.external_id}`);
            return;
          }
          
          // CRITICAL FIX: Handle cross-agent messages gracefully
          // The global WebSocket service now handles saving all messages properly
          const shouldProcessForCurrentUI = data.agent_id === agent.external_id && data.agent_id === currentAgentExternalIdRef.current;
          
          if (!shouldProcessForCurrentUI) {
            console.log(`AgentChat: Message for different agent (${data.agent_id})`);
            console.log(`AgentChat: Current agent: ${agent.external_id}, Current ref: ${currentAgentExternalIdRef.current}`);
            
            // The global WebSocket service will handle saving this message
            console.log(`AgentChat: Global WebSocket service will save this message for agent ${data.agent_id}`);
            return; // Don't update current UI
          }
          
          if (!data.agent_id) {
            console.warn(`AgentChat: WARNING - WebSocket message has no agent_id field, cannot filter properly:`, data);
            return; // Reject messages without agent_id for safety
          }
          
          console.log(`AgentChat: PROCESSING message for correct agent ${agent.external_id}`);
          
          // Create a message hash for deduplication that includes internal agent ID for extra safety
          const messageHash = `${agent.id}-${agent.external_id}-${data.message}-${JSON.stringify(data.data || {})}`;
          if (processedMessages.current.has(messageHash)) {
            console.log(`AgentChat: DUPLICATE - Message already processed, skipping:`, messageHash.substring(0, 100));
            return;
          }
          
          // Mark message as processed
          processedMessages.current.add(messageHash);
          
          // Additional safety check - verify this message should be processed by this specific agent instance
          console.log(`AgentChat: Final check - processing message for agent ${agent.id} (external: ${agent.external_id})`);
          
          console.log(`AgentChat: Message structure - has data field:`, !!data.data, 'waitingForAnswer:', waitingForAnswer, 'waitingForStrategy:', waitingForStrategy);
          
          if (data.message) {
            // Check if this is a final strategy result (has data field)  
            if (data.data) {
              // This message has strategy data, regardless of waiting state
              console.log('AgentChat: Received final strategy result:', data.data);
              
              // Create formatted response for strategy result
              let strategyContent = `I've analyzed your trading instruction.\n\n`;
              
              if (data.data.plan) {
                // Format plan details
                const plan = data.data.plan;
                strategyContent += `**Strategy:** ${plan.goal || plan.strategy_type}\n`;
                if (plan.token_address) strategyContent += `**Token:** ${plan.token_address}\n`;
                if (plan.trade_direction) strategyContent += `**Direction:** ${plan.trade_direction}\n`;
                if (plan.duration_days) strategyContent += `**Duration:** ${plan.duration_days} days\n`;
                strategyContent += `\n`;
              }
              
              strategyContent += `${data.message}\n\n`;
              
              if (data.data.feasible) {
                strategyContent += "✅ This strategy looks feasible and ready to execute.\n\nWould you like to execute this trading plan?";
              } else {
                strategyContent += "⚠️ This strategy is not feasible. Please modify your instruction.\n\nPlease provide a new trading instruction and try again.";
              }
              
              const strategyMessage = {
                id: generateUniqueId(),
                role: 'system',
                content: strategyContent,
                actions: data.data.feasible ? ['confirm'] : []
              };
              
              // Store the parsed strategy if feasible
              if (data.data.feasible && data.data.plan) {
                setParsedStrategy(data.data.plan);
                
                // Also save to localStorage for persistence
                try {
                  const strategyDetailsKey = `agent_strategy_${agent.id}`;
                  const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
                  const parsedDetails = JSON.parse(storedDetails);
                  parsedDetails.parsedStrategy = data.data.plan;
                  localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
                  console.log(`AgentChat: Saved parsed strategy to localStorage for agent ${agent.id}`);
                } catch (error) {
                  console.error('Error saving parsed strategy to localStorage:', error);
                }
              }
              
              // Save to database via API
              const saveStrategyToAPI = async () => {
                try {
                  await tradingAgentApi.addAgentChatMessage(agent.id, strategyMessage.role, strategyMessage.content);
                  console.log('WebSocket strategy result saved to API');
                } catch (error) {
                  console.error('Failed to save WebSocket strategy result to API:', error);
                }
              };
              saveStrategyToAPI();
              
              // Always save to localStorage for persistence across agent switches
              const storageKey = `agent_chat_${agent.id}`;
              try {
                const storedChat = localStorage.getItem(storageKey);
                let existingMessages = [];
                if (storedChat) {
                  const parsed = JSON.parse(storedChat);
                  existingMessages = Array.isArray(parsed) ? parsed : parsed.messages || [];
                }
                
                // Check if this message already exists to prevent duplicates
                const messageExists = existingMessages.some(msg => 
                  msg.role === strategyMessage.role && 
                  msg.content === strategyMessage.content
                );
                
                if (!messageExists) {
                  const updatedMessages = [...existingMessages, strategyMessage];
                  const storageData = {
                    messages: updatedMessages.slice(-50),
                    timestamp: Date.now()
                  };
                  localStorage.setItem(storageKey, JSON.stringify(storageData));
                  console.log(`AgentChat: Saved strategy message to localStorage for agent ${agent.id}`);
                } else {
                  console.log(`AgentChat: Strategy message already exists in localStorage for agent ${agent.id}, skipping save`);
                }
              } catch (e) {
                console.error('Failed to save strategy message to localStorage:', e);
              }

              // Only update UI state if this is the currently active agent
              if (currentAgentExternalIdRef.current === agent.external_id && data.agent_id === agent.external_id) {
                setMessages(prev => [...prev, strategyMessage]);
                console.log(`AgentChat: Updated UI state for strategy message on correct agent ${agent.external_id}`);
              } else {
                console.log(`AgentChat: Saved but did not update UI for strategy message - user is on different agent`);
              }
              
              setWaitingForStrategy(false);
              setLoading(false);
              
            } else {
              // This is either a regular answer or intermediate status message
              const messageContent = data.message;
              console.log('AgentChat: Processing WebSocket message:', messageContent.substring(0, 100) + '...');
              
              // Check if this looks like a final answer (contains detailed info)
              const isFinalAnswer = messageContent.length > 200 && 
                                   (messageContent.includes('Price:') || 
                                    messageContent.includes('Summary') ||
                                    messageContent.includes('24h') ||
                                    messageContent.includes('liquidity'));
              
              if (isFinalAnswer) {
                // This is a final detailed answer
                console.log('AgentChat: Processing final answer');
                const answerMessage = {
                  id: generateUniqueId(),
                  role: 'system',
                  content: messageContent
                };
                
                // Save to database via API
                const saveToAPI = async () => {
                  try {
                    await tradingAgentApi.addAgentChatMessage(agent.id, answerMessage.role, answerMessage.content);
                    console.log('WebSocket message saved to API');
                  } catch (error) {
                    console.error('Failed to save WebSocket message to API:', error);
                  }
                };
                saveToAPI();
                
                // Always save to localStorage for persistence across agent switches
                const storageKey = `agent_chat_${agent.id}`;
                try {
                  const storedChat = localStorage.getItem(storageKey);
                  let existingMessages = [];
                  if (storedChat) {
                    const parsed = JSON.parse(storedChat);
                    existingMessages = Array.isArray(parsed) ? parsed : parsed.messages || [];
                  }
                  
                  // Check if this message already exists to prevent duplicates
                  const messageExists = existingMessages.some(msg => 
                    msg.role === answerMessage.role && 
                    msg.content === answerMessage.content
                  );
                  
                  if (!messageExists) {
                    const updatedMessages = [...existingMessages, answerMessage];
                    const storageData = {
                      messages: updatedMessages.slice(-50),
                      timestamp: Date.now()
                    };
                    localStorage.setItem(storageKey, JSON.stringify(storageData));
                    console.log(`AgentChat: Saved final answer to localStorage for agent ${agent.id}`);
                  } else {
                    console.log(`AgentChat: Final answer already exists in localStorage for agent ${agent.id}, skipping save`);
                  }
                } catch (e) {
                  console.error('Failed to save final answer to localStorage:', e);
                }

                // Only update UI state if this is the currently active agent
                if (currentAgentExternalIdRef.current === agent.external_id && (!data.agent_id || data.agent_id === agent.external_id)) {
                  setMessages(prev => [...prev, answerMessage]);
                  console.log(`AgentChat: Updated UI state for final answer on correct agent ${agent.external_id}`);
                } else {
                  console.log(`AgentChat: Saved but did not update UI for final answer - user is on different agent`);
                }
                
                setWaitingForAnswer(false);
                setWaitingForStrategy(false);
                setLoading(false);
                
              } else if (waitingForStrategy && !data.data) {
                // This is a status update for strategy processing
                console.log('AgentChat: Strategy processing update:', messageContent);
                
                // CRITICAL: Filter out messages that incorrectly show feasibility before we have the actual data
                // These are intermediate processing messages that should NOT show feasibility status
                if (messageContent.includes('⚠️ This strategy is not feasible') || 
                    messageContent.includes('✅ This strategy looks feasible') ||
                    messageContent.includes('Would you like to execute this trading plan?') ||
                    messageContent.includes("I've analyzed your trading instruction") ||
                    messageContent.includes('Failed to parse response as JSON') ||
                    messageContent.includes('Failed to parse')) {
                  console.log('AgentChat: FILTERED - Premature or error message detected, ignoring:', messageContent.substring(0, 100));
                  return; // Don't show premature feasibility messages or parsing errors
                }
                
                // Only show genuine status updates (like "searching for market data", "analyzing", etc.)
                const isGenuineStatusUpdate = 
                  messageContent.toLowerCase().includes('searching') ||
                  messageContent.toLowerCase().includes('analyzing') ||
                  messageContent.toLowerCase().includes('fetching') ||
                  messageContent.toLowerCase().includes('validating') ||
                  messageContent.toLowerCase().includes('processing') ||
                  messageContent.toLowerCase().includes('checking') ||
                  messageContent.toLowerCase().includes('loading');
                
                if (!isGenuineStatusUpdate) {
                  console.log('AgentChat: FILTERED - Non-status message while waiting for strategy:', messageContent.substring(0, 100));
                  return; // Don't show messages that aren't genuine status updates
                }
                
                // Final safety check before updating messages state
                if (currentAgentExternalIdRef.current === agent.external_id && (!data.agent_id || data.agent_id === agent.external_id)) {
                  // Update the last message if it's a processing message, or add new one
                  setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage && lastMessage.role === 'system' && 
                        (lastMessage.content.includes('Processing') || lastMessage.content.includes('processing') ||
                         lastMessage.content.includes('searching') || lastMessage.content.includes('analyzing') ||
                         lastMessage.content.includes('fetching') || lastMessage.content.includes('validating'))) {
                      // Replace the last processing message
                      const updatedMessages = [...prev];
                      updatedMessages[updatedMessages.length - 1] = {
                        ...lastMessage,
                        content: messageContent,
                        id: Date.now()
                      };
                      return updatedMessages;
                    } else {
                      // Add new status message
                      const statusMessage = {
                        id: Date.now(),
                        role: 'system',
                        content: messageContent
                      };
                      return [...prev, statusMessage];
                    }
                  });
                } else {
                  console.log(`AgentChat: BLOCKED state update for strategy processing - agent mismatch`);
                }
                
              } else {
                // Regular answer for questions
                const answerMessage = {
                  id: Date.now(),
                  role: 'system',
                  content: messageContent
                };
                
                // Save to database via API
                const saveToAPI = async () => {
                  try {
                    await tradingAgentApi.addAgentChatMessage(agent.id, answerMessage.role, answerMessage.content);
                    console.log('Regular answer WebSocket message saved to API');
                  } catch (error) {
                    console.error('Failed to save regular answer WebSocket message to API:', error);
                  }
                };
                saveToAPI();
                
                // Final safety check before updating messages state
                if (currentAgentExternalIdRef.current === agent.external_id && (!data.agent_id || data.agent_id === agent.external_id)) {
                  setMessages(prev => {
                    const updatedMessages = [...prev, answerMessage];
                    // Save to localStorage
                    const storageKey = `agent_chat_${agent.id}`;
                    const storageData = {
                      messages: updatedMessages.slice(-50),
                      timestamp: Date.now()
                    };
                    try {
                      localStorage.setItem(storageKey, JSON.stringify(storageData));
                    } catch (e) {
                      console.error('Failed to save chat to localStorage:', e);
                    }
                    return updatedMessages;
                  });
                } else {
                  console.log(`AgentChat: BLOCKED state update for regular answer - agent mismatch`);
                }
                
                setWaitingForAnswer(false);
                setLoading(false);
              }
            }
          }
        } catch (error) {
          console.error('AgentChat: Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error(`AgentChat: WebSocket error:`, error);
      };
      
      ws.onclose = () => {
        console.log(`AgentChat: WebSocket disconnected for agent ${agent.id}`);
        // Attempt to reconnect after 3 seconds
        setTimeout(() => {
          if (agent && agent.external_id) {
            connectWebSocket();
          }
        }, 3000);
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    
    // Cleanup on unmount or agent change
    return () => {
      if (wsRef.current) {
        console.log(`AgentChat: Closing WebSocket for agent ${agent?.id}`);
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Stop global WebSocket service connection for this agent
      if (agent?.id && agent?.external_id) {
        globalWebSocketService.stopConnection(agent.id, agent.external_id, 'chat');
      }
      
      // Clear the current agent ref to prevent stale state updates
      currentAgentExternalIdRef.current = null;
      console.log(`AgentChat: Cleared agent ref on cleanup`);
    };
  }, [agent?.external_id]);
  
  // Store chat history in local cache by agent ID
  const chatCache = useRef(new Map());
  
  // // Fetch user's wallets on component mount
  // useEffect(() => {
  //   const fetchWallets = async () => {
  //     try {
  //       const fetchedWallets = await walletApi.getUserWallets();
  //       setWallets(fetchedWallets);
        
  //       // If we have a default wallet, auto-select it
  //       const defaultWallet = fetchedWallets.find(w => w.is_default);
  //       if (defaultWallet) {
  //         setSelectedWallet(defaultWallet);
  //         setWalletAddresses([defaultWallet.address]);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching wallets:', error);
  //     }
  //   };
    
  //   fetchWallets();
  // }, []);
  



  // Maintain a timestamp for group wallet fetches to avoid duplicate requests
  const lastFetchTimestamps = useRef({
    groups: 0,
    groupWallets: {}
  });
  
  // Define minimum time between fetches (5 seconds)
  const MIN_FETCH_INTERVAL_MS = 5000;
  
  // Helper to check if we should fetch based on last fetch time
  const shouldFetch = (type, groupId = null) => {
    const now = Date.now();
    
    if (type === 'groups') {
      const timeSinceLastFetch = now - lastFetchTimestamps.current.groups;
      if (timeSinceLastFetch < MIN_FETCH_INTERVAL_MS) {
        console.log(`Skipping groups fetch - last fetch was ${timeSinceLastFetch}ms ago`);
        return false;
      }
      lastFetchTimestamps.current.groups = now;
      return true;
    }
    
    if (type === 'groupWallets' && groupId) {
      const lastGroupFetch = lastFetchTimestamps.current.groupWallets[groupId] || 0;
      const timeSinceLastFetch = now - lastGroupFetch;
      if (timeSinceLastFetch < MIN_FETCH_INTERVAL_MS) {
        console.log(`Skipping group ${groupId} wallets fetch - last fetch was ${timeSinceLastFetch}ms ago`);
        return false;
      }
      lastFetchTimestamps.current.groupWallets[groupId] = now;
      return true;
    }
    
    return true;
  };
  
  useEffect(() => {
  if (!agent) return;
  
  // Just load the saved wallet or group for this agent, but don't connect automatically
  const loadSavedWalletOrGroup = async () => {
    try {
      // Check if there's a selected wallet/group for this agent
      const strategyDetailsKey = `agent_strategy_${agent.id}`;
      const storedDetails = localStorage.getItem(strategyDetailsKey);
      
      if (storedDetails) {
        try {
          const parsedDetails = JSON.parse(storedDetails);
          
          // Check if we have a group ID saved
          if (parsedDetails.groupId) {
            // Only fetch groups if enough time has passed
            if (shouldFetch('groups')) {
              const groups = await walletApi.getWalletGroups();
              const savedGroup = groups.find(g => g.id === parsedDetails.groupId);
              
              if (savedGroup) {
                console.log(`Found saved group ${savedGroup.name} for agent ${agent.id}`);
                setSelectedGroup(savedGroup);
                
                // Fetch wallets in the group only if enough time has passed
                if (shouldFetch('groupWallets', savedGroup.id)) {
                  const response = await walletApi.getUserWallets(savedGroup.id);
                  setGroupWallets(response.wallets || []);
                  
                  // Set wallet addresses
                  if (parsedDetails.walletAddresses && parsedDetails.walletAddresses.length > 0) {
                    setWalletAddresses(parsedDetails.walletAddresses);
                  } else if (response.wallets && response.wallets.length > 0) {
                    setWalletAddresses(response.wallets.map(w => w.address));
                  }
                }
              }
            }
          }
          // If no group but we have wallet addresses
          else if (parsedDetails.walletAddresses && parsedDetails.walletAddresses.length > 0) {
            const savedAddress = parsedDetails.walletAddresses[0];
            // Find the matching wallet
            const matchingWallet = wallets.find(w => w.address === savedAddress);
            if (matchingWallet) {
              console.log(`Found saved wallet ${matchingWallet.address} for agent ${agent.id}`);
              setSelectedWallet(matchingWallet);
              setWalletAddresses([matchingWallet.address]);
            }
          }
        } catch (e) {
          console.error('Error parsing saved agent strategy details:', e);
        }
      } else if (wallets.length > 0 && !selectedWallet && !selectedGroup) {
        // If no saved wallet/group but we have wallets, select default or first
        const defaultWallet = wallets.find(w => w.is_default) || wallets[0];
        if (defaultWallet) {
          console.log(`Using default wallet ${defaultWallet.address} for agent ${agent.id}`);
          setSelectedWallet(defaultWallet);
          setWalletAddresses([defaultWallet.address]);
        }
      }
    } catch (error) {
      console.error('Error loading saved wallet or group:', error);
    }
  };
  
  loadSavedWalletOrGroup();
}, [agent, wallets, selectedWallet, selectedGroup]);


  
  // Handle wallet selection
const handleWalletSelect = (wallet) => {
  setSelectedWallet(wallet);
  if (wallet && wallet.address) {
    setWalletAddresses([wallet.address]);
    
    // Save wallet address for current agent
    if (agent && agent.id) {
      try {
        const strategyDetailsKey = `agent_strategy_${agent.id}`;
        const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
        const parsedDetails = JSON.parse(storedDetails);
        parsedDetails.walletAddresses = [wallet.address];
        localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
        
        // Notify parent components of wallet selection
        if (onWalletSelect) {
          onWalletSelect(wallet);
        }
      } catch (error) {
        console.error('Error saving wallet address:', error);
      }
    }
  }
};

  // Debug helper to check localStorage state
  const debugStorageState = (agentId) => {
    try {
      const storageKey = `agent_chat_${agentId}`;
      const storedChat = localStorage.getItem(storageKey);
      if (storedChat) {
        console.log(`[DEBUG] Found ${JSON.parse(storedChat).length} stored messages for agent ${agentId}`);
      } else {
        console.log(`[DEBUG] No stored messages for agent ${agentId}`);
      }
    } catch (error) {
      console.error('[DEBUG] Error checking storage state:', error);
    }
  };
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  // Save to localStorage with a specific key for this agent
  const saveLocalChat = (agentId, messages) => {
    if (!agentId) {
      console.error('Cannot save chat: Invalid agent ID');
      return;
    }
    
    try {
      const storageKey = `agent_chat_${agentId}`;
      // Only keep the last 50 messages to avoid storage limits
      const messagesToStore = messages.slice(-50);
      localStorage.setItem(storageKey, JSON.stringify(messagesToStore));
      
      // Also update our in-memory cache
      chatCache.current.set(agentId, messagesToStore);
      
      console.log(`Saved ${messagesToStore.length} messages to local storage for agent ${agentId}`);
    } catch (error) {
      console.error('Error saving chat to localStorage:', error);
    }
  };
  
  // Load from localStorage for this specific agent
  const getLocalChat = (agentId) => {
    if (!agentId) {
      console.error('Cannot get chat: Invalid agent ID');
      return null;
    }
    
    // First check our in-memory cache
    if (chatCache.current.has(agentId)) {
      console.log(`Retrieved ${chatCache.current.get(agentId).length} messages from memory cache for agent ${agentId}`);
      return chatCache.current.get(agentId);
    }
    
    // If not in memory, try localStorage
    try {
      const storageKey = `agent_chat_${agentId}`;
      const storedChat = localStorage.getItem(storageKey);
      if (storedChat) {
        const parsedChat = JSON.parse(storedChat);
        // Update our cache
        chatCache.current.set(agentId, parsedChat);
        return parsedChat;
      }
    } catch (error) {
      console.error('Error retrieving chat from localStorage:', error);
    }
    return null;
  };
  
const saveMessage = async (role, content) => {
  if (!agent || !agent.id) {
    console.error('Cannot save message: No active agent');
    return;
  }
  
  // Create a message object
  const messageData = {
    role,
    content
  };
  
  try {
    // Always try to save to API first
    let savedMessage;
    
    try {
      console.log(`Saving message to API for agent ${agent.id}:`, { role, content: content.substring(0, 50) + '...' });
      savedMessage = await tradingAgentApi.addAgentChatMessage(agent.id, role, content);
      console.log(`Message saved to API for agent ${agent.id} with ID:`, savedMessage.id);
      
      // If API save is successful, clear the usingLocalStorage flag
      if (usingLocalStorage) {
        setUsingLocalStorage(false);
      }
    } catch (apiError) {
      console.error(`Failed to save message to API for agent ${agent.id}, falling back to localStorage:`, apiError);
      setUsingLocalStorage(true);
      
      // Create a fallback message object for localStorage
      savedMessage = {
        id: Date.now(),
        agent_id: agent.id,
        role,
        content,
        timestamp: new Date().toISOString()
      };
    }
    
    // Check that agent ID still matches current agent
    if (previousAgentIdRef.current !== agent.id) {
      console.log(`Agent changed during message save. Aborting local save.`);
      return;
    }
    
    // Always update local storage and memory cache regardless of API success
    const updatedMessages = [...messages, savedMessage];
    saveLocalChat(agent.id, updatedMessages);
    
    return savedMessage;
  } catch (error) {
    console.error(`Failed to save message for agent ${agent.id}:`, error);
  }
};

  // Load chat history when agent changes
  useEffect(() => {
    if (!agent || !agent.id) return;
    
    // Check if we should prevent chat reload (during wallet generation)
    const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
    const shouldPreventReload = sessionStorage.getItem(preventReloadFlag);
    if (shouldPreventReload) {
      console.log(`[CHAT LOADING] Skipping chat reload for agent ${agent.id} - wallet generation in progress`);
      return;
    }
    
    setInitialLoading(true);
    
    // Store current agent ID in ref to track changes
    const currentAgentId = agent.id;
    const previousAgentId = previousAgentIdRef.current;
    
    // If we're switching agents, save current state to cache for the previous agent
    if (previousAgentId && previousAgentId !== currentAgentId && messages.length > 0) {
      console.log(`Saving chat state for previous agent ${previousAgentId} before switching`);
      saveLocalChat(previousAgentId, messages);
    }
    
    // Update the ref to current agent ID
    previousAgentIdRef.current = currentAgentId;
    
    console.log(`Loading chat history for agent ${currentAgentId}`);
    
    const loadChatHistory = async () => {
      console.log(`[CHAT LOADING] Starting loadChatHistory for agent ${currentAgentId}`);
      try {
        let chatHistory = [];
        
        // ALWAYS try API first to get the most recent messages (including those received while away)
        try {
          console.log(`[CHAT LOADING] Fetching fresh chat history for agent ${currentAgentId} from API`);
          chatHistory = await tradingAgentApi.getAgentChat(currentAgentId);
          console.log(`[CHAT LOADING] Received ${chatHistory.length} messages from API for agent ${currentAgentId}`);
          console.log(`[CHAT LOADING] First 3 messages:`, chatHistory.slice(0, 3));
          
          // Update local cache with this fresh data (this replaces any stale localStorage data)
          if (chatHistory && chatHistory.length > 0) {
            saveLocalChat(currentAgentId, chatHistory);
            // Clear the processed messages set to allow reprocessing of any new messages
            processedMessages.current.clear();
          }
          
          // Clear the usingLocalStorage flag since API worked
          if (usingLocalStorage) {
            setUsingLocalStorage(false);
          }
        } catch (apiError) {
          console.log(`API chat history fetch failed for agent ${currentAgentId}, using local storage:`, apiError);
          setUsingLocalStorage(true);
          
          // Only use localStorage if API failed completely
          const localChat = getLocalChat(currentAgentId);
          if (localChat && localChat.length > 0) {
            console.log(`Retrieved ${localChat.length} messages from local storage for agent ${currentAgentId}`);
            chatHistory = localChat;
          }
        }
        
        // Verify we're still working with the same agent
        if (previousAgentIdRef.current !== currentAgentId) {
          console.log(`[CHAT LOADING] Agent changed during chat history loading. Aborting. Previous: ${previousAgentIdRef.current}, Current: ${currentAgentId}`);
          return;
        }
        
        // If we have chat history, use it
        if (chatHistory && chatHistory.length > 0) {
          console.log(`[CHAT LOADING] Processing ${chatHistory.length} messages for display`);
          
          // Deduplicate messages by content and timestamp to prevent localStorage duplicates
          const seenMessages = new Set();
          const deduplicatedHistory = chatHistory.filter((msg) => {
            const messageKey = `${msg.role}-${msg.content}-${msg.timestamp || msg.created_at}`;
            if (seenMessages.has(messageKey)) {
              console.log(`[CHAT LOADING] Filtering out duplicate message: ${msg.content?.substring(0, 50)}...`);
              return false;
            }
            seenMessages.add(messageKey);
            return true;
          });
          
          if (deduplicatedHistory.length !== chatHistory.length) {
            console.log(`[CHAT LOADING] Removed ${chatHistory.length - deduplicatedHistory.length} duplicate messages`);
          }
          
          // Format messages for display
          const formattedMessages = deduplicatedHistory.map((msg, index) => ({
            id: msg.id || Date.now() + index,
            role: msg.role,
            content: msg.content,
            isError: msg.content.includes("Error:") || msg.content.includes("Sorry, I couldn't process"),
            actions: msg.role === 'system' && 
                    msg.content.includes("✅ This strategy looks feasible") && 
                    !agent.active ? ['confirm'] : []
          }));
          
          console.log(`[CHAT LOADING] Setting ${formattedMessages.length} formatted messages in UI`);
          console.log(`[CHAT LOADING] Sample formatted messages:`, formattedMessages.slice(0, 3));
          setMessages(formattedMessages);
          console.log(`[CHAT LOADING] setMessages called successfully`);
          
          // Check if we need to restore the parsed strategy
          // Look for the last feasible strategy message and restore the parsed strategy
          const lastFeasibleMessage = formattedMessages.reverse().find(msg => 
            msg.role === 'system' && 
            msg.content.includes("✅ This strategy looks feasible") &&
            msg.actions && msg.actions.includes('confirm')
          );
          formattedMessages.reverse(); // Restore original order
          
          if (lastFeasibleMessage && !agent.active) {
            // Try to restore the parsed strategy from localStorage
            try {
              const strategyDetailsKey = `agent_strategy_${currentAgentId}`;
              const storedDetails = localStorage.getItem(strategyDetailsKey);
              if (storedDetails) {
                const parsedDetails = JSON.parse(storedDetails);
                if (parsedDetails.parsedStrategy) {
                  console.log(`Restored parsed strategy for agent ${currentAgentId}:`, parsedDetails.parsedStrategy);
                  setParsedStrategy(parsedDetails.parsedStrategy);
                }
              }
            } catch (error) {
              console.error('Error restoring parsed strategy:', error);
            }
          }
          
          // Only add monitoring message if agent is active AND it's not already there
          if (agent.active && !formattedMessages.some(msg => 
            msg.role === 'system' && msg.content.includes("I'm monitoring your active strategy")
          )) {
            const activeMessage = {
              id: Date.now(),
              role: 'system',
              content: "I'm monitoring your active strategy. You can continue to chat with me while it's running. If you want to adjust the strategy, please first stop the current execution using the controls in the terminal panel."
            };
            
            if (previousAgentIdRef.current === currentAgentId) {
              setTimeout(() => {
                if (previousAgentIdRef.current === currentAgentId) {
                  setMessages(currentMsgs => {
                    const updatedMsgs = [...currentMsgs, activeMessage];
                    // Save to storage
                    saveLocalChat(currentAgentId, updatedMsgs);
                    return updatedMsgs;
                  });
                }
              }, 100);
            }
          }
        } else {
          // Initialize with welcome message if no history
          console.log(`No chat history found, initializing with welcome message for agent ${currentAgentId}`);
          
          const welcomeMessage = { 
            id: Date.now(), 
            role: 'system', 
            content: `Welcome to ${formatAgentName(agent.name)}! How can I help you today? You can ask me to trade cryptocurrency, view your wallets, or generate new wallets for you.` 
          };
          
          setMessages([welcomeMessage]);
          
          // Save welcome message
          await saveMessage('system', welcomeMessage.content);
        }
      } catch (error) {
        console.error(`Failed to load chat history for agent ${currentAgentId}:`, error);
        
        // Fall back to welcome message on error
        if (previousAgentIdRef.current === currentAgentId) {
          const welcomeMessage = { 
            id: Date.now(), 
            role: 'system', 
            content: `Welcome to ${formatAgentName(agent.name)}! How can I help you today? You can ask me to trade cryptocurrency, view your wallets, or generate new wallets for you.` 
          };
          
          setMessages([welcomeMessage]);
          
          // Save welcome message
          await saveMessage('system', welcomeMessage.content);
        }
      } finally {
        // Only update if we're still on the same agent
        if (previousAgentIdRef.current === currentAgentId) {
          setInitialLoading(false);
        }
      }
    };
    
    loadChatHistory();
    
    // Also check if we have saved token address and wallet addresses for this agent
    try {
      const strategyDetailsKey = `agent_strategy_${currentAgentId}`;
      const storedDetails = localStorage.getItem(strategyDetailsKey);
      let hasStoredTokenAddress = false;

      if (storedDetails) {
        const parsedDetails = JSON.parse(storedDetails);
        if (parsedDetails.tokenAddress) {
          setTokenAddress(parsedDetails.tokenAddress);
          hasStoredTokenAddress = true;
        }
        if (parsedDetails.walletAddresses) {
          setWalletAddresses(parsedDetails.walletAddresses);

          // Also try to select the wallet in the dropdown
          if (parsedDetails.walletAddresses.length > 0 && wallets.length > 0) {
            const matchingWallet = wallets.find(w => w.address === parsedDetails.walletAddresses[0]);
            if (matchingWallet) {
              setSelectedWallet(matchingWallet);
            }
          }
        }
      }

      // Auto-fill token address from current token context - prioritize current token's mint address
      if (tokenData && tokenData.mint_address) {
        const currentTokenAddress = tokenData.mint_address;
        const storedTokenAddress = storedDetails ? JSON.parse(storedDetails).tokenAddress : null;

        if (currentTokenAddress && currentTokenAddress !== storedTokenAddress) {
          console.log(`Auto-filling token address from localStorage load for agent ${currentAgentId}:`, currentTokenAddress);
          setTokenAddress(currentTokenAddress);

          // Save the auto-filled token address to localStorage
          const updatedDetails = storedDetails ? JSON.parse(storedDetails) : {};
          updatedDetails.tokenAddress = currentTokenAddress;
          localStorage.setItem(strategyDetailsKey, JSON.stringify(updatedDetails));
        }
      }
    } catch (error) {
      console.error('Error loading saved strategy details:', error);
    }
    
  }, [agent?.id, wallets]); // Only depend on agent.id and wallets
  
  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-fill token address when tokenData becomes available or fetch it if needed
  useEffect(() => {
    if (!agent?.id) return;

    const fillTokenAddress = async () => {
      let currentTokenAddress = null;

      console.log(`🔍 Starting token address fill for agent ${agent.id}`, {
        tokenId,
        hasTokenData: !!tokenData,
        currentTokenAddress: tokenAddress,
        location: window.location.href
      });

      // First try to get from tokenData - prioritize mint_address
      if (tokenData && tokenData.mint_address) {
        currentTokenAddress = tokenData.mint_address;
        console.log(`✅ Using mint_address from tokenData for agent ${agent.id}:`, currentTokenAddress);
      } else {
        console.log(`❌ No tokenData.mint_address found. TokenData:`, tokenData);
      }

      // If no tokenData but we have tokenId, try to fetch token data from API
      if (!currentTokenAddress && tokenId) {
        try {
          console.log(`🌐 Fetching token data from API for tokenId: ${tokenId}`);

          // Try to fetch specific token data from API endpoint
          const response = await api.get(`/available-tokens/${tokenId}`);
          const token = response.data;

          console.log(`🎯 Received token data from API:`, token);

          if (token && token.mint_address) {
            currentTokenAddress = token.mint_address;
            console.log(`✅ Using mint_address from API for token ${tokenId}:`, currentTokenAddress);
          } else {
            console.log(`❌ Token found but missing mint_address. Token data:`, token);
          }
        } catch (error) {
          console.error('🚨 Error fetching token data from API:', error);
          console.error('API Error details:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
        }
      }

      // Final fallback: Check if tokenId looks like a valid Solana address
      if (!currentTokenAddress && tokenId && tokenId.length >= 32 && /^[1-9A-HJ-NP-Za-km-z]+$/.test(tokenId)) {
        currentTokenAddress = tokenId;
        console.log(`🔄 Using tokenId as valid address fallback for agent ${agent.id}:`, currentTokenAddress);
      }

      // If we have a token address and it's different from current, update it
      if (currentTokenAddress && currentTokenAddress !== tokenAddress) {
        console.log(`🔥 Setting token address for agent ${agent.id}:`, currentTokenAddress);
        setTokenAddress(currentTokenAddress);

        // Save to localStorage
        try {
          const strategyDetailsKey = `agent_strategy_${agent.id}`;
          const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
          const parsedDetails = JSON.parse(storedDetails);
          parsedDetails.tokenAddress = currentTokenAddress;
          localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
          console.log(`💾 Saved token address to localStorage`);
        } catch (error) {
          console.error('🚨 Error saving auto-filled token address:', error);
        }
      } else if (!currentTokenAddress) {
        console.log(`⚠️ No token address could be determined for agent ${agent.id}`);
      } else {
        console.log(`ℹ️ Token address unchanged for agent ${agent.id}:`, currentTokenAddress);
      }

      // Final debug summary
      console.log(`📋 Token address fill summary for agent ${agent.id}:`, {
        tokenId,
        hasTokenData: !!tokenData,
        tokenDataMintAddress: tokenData?.mint_address,
        resolvedTokenAddress: currentTokenAddress,
        existingTokenAddress: tokenAddress,
        willUpdate: currentTokenAddress && currentTokenAddress !== tokenAddress
      });
    };

    fillTokenAddress();
  }, [tokenData, tokenId, agent?.id, tokenAddress]);


const parseInstructionWithErrorHandling = async (agentId, userInput, tokenAddress, walletAddresses) => {
  try {
    // Keep track of attempts
    let attempts = 0;
    const maxAttempts = 3;
    let lastError = null;
    let lastErrorData = null;
    
    // Try to parse the instruction with the API, with retries
    while (attempts < maxAttempts) {
      try {
        attempts++;
        console.log(`Attempt ${attempts} to parse instruction for agent ${agentId}`);
        
        // Call the API to parse the instruction
        const result = await tradingAgentApi.parseInstruction(
          agentId, 
          userInput,
          tokenAddress || undefined,
          walletAddresses.length > 0 ? walletAddresses : undefined
        );
        
        // Check if the result contains error messages even if status was 200
        if (result.error || 
            (result.success === false) || 
            (result.details && result.details.includes("error")) ||
            (result.message && result.message.toLowerCase().includes("error"))) {
          
          console.error(`API returned error despite 200 status:`, result);
          // Store the data for a better error message
          lastErrorData = result;
          throw new Error(result.error || result.message || "API indicated failure in response");
        }
        
        // Also check for static fallback values that should be recognized and rejected
        if (result.parsedPlan && 
            result.parsedPlan.strategy === "GRID_TRADING" && 
            result.parsedPlan.pair === "SOL/USDC" &&
            result.parsedPlan.upperLimit === 15 &&
            result.parsedPlan.lowerLimit === 10 &&
            result.parsedPlan.gridLevels === 3 &&
            result.parsedPlan.investment === 1000) {
          
          console.error(`Detected static fallback data in the response:`, result);
          lastErrorData = { explanation: "Received static mock data. Please ensure the trading service is properly connected." };
          throw new Error("Received static fallback data from backend");
        }
        
        // If we get here, the call was successful with no errors, so return the result
        return {
          success: true,
          data: result
        };
      } catch (error) {
        console.error(`Error on attempt ${attempts}:`, error);
        lastError = error;
        
        // If we've reached the maximum number of attempts, break out of the loop
        if (attempts >= maxAttempts) {
          break;
        }
        
        // Wait a bit before trying again (increase delay with each attempt)
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }
    
    // If we've reached this point, all attempts failed
    // Use the explanation from the API response if available
    let errorMessage;
    if (lastErrorData && lastErrorData.explanation) {
      errorMessage = lastErrorData.explanation;
    } else if (lastErrorData && lastErrorData.message) {
      errorMessage = lastErrorData.message;
    } else {
      errorMessage = "Unable to connect to trading agent after multiple attempts. The service may be experiencing high traffic or market data issues. Please try again later.";
    }
    
    return {
      success: false,
      error: lastError,
      errorMessage: errorMessage,
      errorData: lastErrorData
    };
  } catch (error) {
    return {
      success: false,
      error,
      errorMessage: "An unexpected error occurred while communicating with the trading agent. The service may be temporarily unavailable."
    };
  }
};
  const handleGroupSelect = async (group) => {
  setSelectedGroup(group);
  
  // Check if group is valid
  if (!group || !group.id) {
    // Clearing group selection is normal behavior
    if (group === null) {
      console.log('Clearing group selection');
    } else {
      console.error('Invalid group selected:', group);
    }
    setGroupWallets([]);
    return;
  }
  
  // Fetch wallets in the group
  try {
    const response = await walletApi.getUserWallets(group.id);
    setGroupWallets(response.wallets || []);
    
    // Set wallet addresses from all wallets in the group
    const addresses = response.wallets.map(w => w.address);
    setWalletAddresses(addresses);
    
    // Save group selection for current agent
    if (agent && agent.id) {
      try {
        const strategyDetailsKey = `agent_strategy_${agent.id}`;
        const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
        const parsedDetails = JSON.parse(storedDetails);
        parsedDetails.groupId = group.id;
        parsedDetails.walletAddresses = addresses;
        localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
      } catch (error) {
        console.error('Error saving group selection:', error);
      }
    }
    
    // Clear individual wallet selection
    setSelectedWallet(null);
  } catch (error) {
    console.error('Error fetching group wallets:', error);
  }
};

// Generate a new wallet
const handleGenerateWallet = async () => {
  try {
    setGeneratingWallet(true);
    
    // Prevent chat reload during wallet generation
    const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
    sessionStorage.setItem(preventReloadFlag, 'true');
    
    // Generate a name based on the agent
    const walletName = agent ? `${agent.name} Wallet` : `New Wallet ${new Date().toLocaleString()}`;
    
    // Call API to generate wallet for current token
    const newWallet = await walletApi.generateWallet(walletName, tokenId);
    
    // Update wallets list
    setWallets(prev => [newWallet, ...prev]);
    
    // Set the new wallet as the active one
    setSelectedWallet(newWallet);
    setWalletAddresses([newWallet.address]);
    
    // Add system message with wallet details
    const walletMessage = {
      id: Date.now(),
      role: 'system',
      content: `I've generated a new Solana wallet for you:

**Wallet Name:** ${newWallet.name}
**Public Address:** ${newWallet.address}

Your wallet has been securely created and stored.`,
      isWalletGeneration: true
    };
    
    // Add excel file with wallet details (excluding private keys for security)
    const excelData = [
      ['Wallet Name', 'Public Address'],
      [newWallet.name, newWallet.address]
    ];
    
    walletMessage.fileData = {
      type: 'excel',
      filename: 'wallet_info.xlsx',
      data: excelData
    };
    
    setMessages(prev => {
      const updatedMessages = [...prev, walletMessage];
      // Save to storage (without private key - we never store this in browser)
      const messageToStore = {...walletMessage};
      messageToStore.content = messageToStore.content.replace(newWallet.private_key, '[REDACTED]');
      delete messageToStore.fileData; // Don't store file data in chat history
      saveLocalChat(agent.id, [...prev, messageToStore]);
      return updatedMessages;
    });

    // Also save to API
    saveMessage('system', walletMessage.content).catch(err => {
      console.error('[AgentChat] Failed to save wallet generation message to API:', err);
    });
    
    // Clear the prevent reload flag after a short delay to allow wallet selection to complete
    setTimeout(() => {
      const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
      sessionStorage.removeItem(preventReloadFlag);
      console.log('[AgentChat] Chat reload prevention flag cleared');
    }, 2000);
    
  } catch (error) {
    console.error('Error generating wallet:', error);
    
    // Add error message
    const errorMessage = {
      id: Date.now(),
      role: 'system',
      content: `Failed to generate wallet: ${error.message}`,
      isError: true
    };
    
    setMessages(prev => {
      const updatedMessages = [...prev, errorMessage];
      saveLocalChat(agent.id, updatedMessages);
      return updatedMessages;
    });
    
    // Clear the prevent reload flag in error case too
    const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
    sessionStorage.removeItem(preventReloadFlag);
  } finally {
    setGeneratingWallet(false);
  }
};

  // Handle generating multiple wallets
  const handleGenerateMultipleWallets = async (count) => {
    try {
      setGeneratingWallet(true);
      
      // Prevent chat reload during wallet generation
      const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
      sessionStorage.setItem(preventReloadFlag, 'true');
      
      // Validate count
      const walletCount = parseInt(count);
      if (isNaN(walletCount) || walletCount < 1 || walletCount > 10) {
        throw new Error('Please specify a valid number between 1 and 10');
      }
      
      // Generate a name prefix based on the agent
      const namePrefix = agent ? `${agent.name} Wallet` : `New Wallet`;
      
      // Call API to generate wallets
      const result = await walletApi.generateMultipleWallets(walletCount, namePrefix);
      
      // Update wallets list
      setWallets(prev => [...result.wallets, ...prev]);
      
      // Set the first new wallet as the active one
      if (result.wallets.length > 0) {
        setSelectedWallet(result.wallets[0]);
        setWalletAddresses([result.wallets[0].address]);
      }
      
      // Create Excel data
      const excelData = [
        ['Wallet Name', 'Public Address', 'Private Key', 'Created Date'],
        ...result.wallets.map(wallet => [
          wallet.name,
          wallet.address,
          wallet.private_key,
          new Date(wallet.created_at).toLocaleString()
        ])
      ];
      
      // Add system message with wallet details
      const walletMessage = {
        id: Date.now(),
        role: 'system',
        content: `I've generated ${result.wallets.length} new Solana wallet${result.wallets.length > 1 ? 's' : ''} for you:\n\n${
          result.wallets.map((wallet, index) => 
            `**Wallet ${index+1}: ${wallet.name}**\n` +
            `**Public Address:** ${wallet.address}\n`
          ).join('\n')
        }\nYour wallets have been securely created and stored.`,
        isWalletGeneration: true
      };
      
      // Add excel file with wallet details
      walletMessage.fileData = {
        type: 'excel',
        filename: `wallet_info_${new Date().toISOString().slice(0, 10)}.xlsx`,
        data: excelData
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, walletMessage];
        // Save to storage
        const messageToStore = {...walletMessage};
        delete messageToStore.fileData; // Don't store file data in chat history
        saveLocalChat(agent.id, [...prev, messageToStore]);
        return updatedMessages;
      });

      // Also save to API
      saveMessage('system', walletMessage.content).catch(err => {
        console.error('[AgentChat] Failed to save wallet generation message to API:', err);
      });
      
      // Clear the prevent reload flag after a short delay
      setTimeout(() => {
        const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
        sessionStorage.removeItem(preventReloadFlag);
        console.log('[AgentChat] Multiple wallets - Chat reload prevention flag cleared');
      }, 2000);
      
    } catch (error) {
      console.error('Error generating multiple wallets:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now(),
        role: 'system',
        content: `Failed to generate wallets: ${error.message}`,
        isError: true
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
      
      // Clear the prevent reload flag in error case too
      const preventReloadFlag = `prevent_chat_reload_${agent.id}`;
      sessionStorage.removeItem(preventReloadFlag);
    } finally {
      setGeneratingWallet(false);
    }
  };
  
  // Handle displaying all wallets
  const handleDisplayWallets = async () => {
    try {
      setLoading(true);
      
      // Fetch fresh data
      const fetchedData = await walletApi.getUserWallets();
      const fetchedWallets = fetchedData.wallets || [];
      
      if (fetchedWallets.length === 0) {
        // No wallets found
        const noWalletsMessage = {
          id: Date.now(),
          role: 'system',
          content: `You don't have any wallets yet. Would you like me to generate one for you?`
        };
        
        setMessages(prev => {
          const updatedMessages = [...prev, noWalletsMessage];
          saveLocalChat(agent.id, updatedMessages);
          return updatedMessages;
        });
        
        return;
      }
      
      // Create a message with wallet list
      let walletsContent = `Here are your wallets:\n\n`;
      
      fetchedWallets.forEach((wallet, index) => {
        walletsContent += `**Wallet ${index + 1}: ${wallet.name || 'Unnamed Wallet'}**\n`;
        walletsContent += `Address: ${wallet.address}\n`;
        walletsContent += `Created: ${new Date(wallet.created_at).toLocaleString()}\n\n`;
      });
      
      walletsContent += `To view your private keys or export all wallet details, please ask me to "export wallets as Excel".`;
      
      const walletsMessage = {
        id: Date.now(),
        role: 'system',
        content: walletsContent
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, walletsMessage];
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
      
    } catch (error) {
      console.error('Error fetching wallets:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now(),
        role: 'system',
        content: `Failed to fetch wallets: ${error.message}`,
        isError: true
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle exporting all wallets as Excel
  const handleExportWallets = async () => {
    try {
      setLoading(true);
      
      // Get all wallets with private keys for current token
      const allWallets = await walletApi.getWalletsForChatbot(tokenId);
      
      if (allWallets.length === 0) {
        // No wallets found
        const noWalletsMessage = {
          id: Date.now(),
          role: 'system',
          content: `You don't have any wallets yet. Would you like me to generate one for you?`
        };
        
        setMessages(prev => {
          const updatedMessages = [...prev, noWalletsMessage];
          saveLocalChat(agent.id, updatedMessages);
          return updatedMessages;
        });
        
        return;
      }
      
      // Create Excel data (excluding private keys for security)
      const excelData = [
        ['Wallet Name', 'Public Address', 'Created Date'],
        ...allWallets.map(wallet => [
          wallet.name || 'Unnamed Wallet',
          wallet.address,
          new Date(wallet.created_at).toLocaleString()
        ])
      ];
      
      // Create a message with the Excel file
      const exportMessage = {
        id: Date.now(),
        role: 'system',
        content: `Here is your wallet export file with ${allWallets.length} wallets.

This file contains wallet names, public addresses, and creation dates.`,
        fileData: {
          type: 'excel',
          filename: 'wallet_export.xlsx',
          data: excelData
        }
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, exportMessage];
        // Save to storage (without file data)
        const messageToStore = {...exportMessage};
        delete messageToStore.fileData;
        saveLocalChat(agent.id, [...prev, messageToStore]);
        return updatedMessages;
      });
      
    } catch (error) {
      console.error('Error exporting wallets:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now(),
        role: 'system',
        content: `Failed to export wallets: ${error.message}`,
        isError: true
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
    } finally {
      setLoading(false);
    }
  };
  
const handleSendMessage = async () => {
  console.log('[AgentChat] handleSendMessage called');
  console.log('[AgentChat] Current state:', { 
    input: input, 
    loading: loading, 
    agent: agent,
    agentId: agent?.id 
  });
  
  // Prevent any default behavior
  if (!input.trim() || loading) {
    console.log('[AgentChat] Message send blocked:', {
      emptyInput: !input.trim(),
      loading: loading,
      inputValue: input
    });
    return;
  }
  
  if (!agent || !agent.id) {
    console.error('[AgentChat] Cannot send message: No active agent', { agent });
    return;
  }
  
  // Add user message locally
  const userMessage = { id: generateUniqueId(), role: 'user', content: input };
  setMessages(prev => [...prev, userMessage]);
  
  const userInput = input;
  setInput('');
  setLoading(true);
  
  // Save user message (non-blocking)
  saveMessage('user', userInput).catch(err => {
    console.error('[AgentChat] Failed to save user message:', err);
  });
  
  try {
    console.log(`[AgentChat] Starting message processing for: "${userInput}"`);
    
    // Check if this is a request to view or manage wallets
    const lowerInput = userInput.toLowerCase();
    console.log(`[AgentChat] Processing user input: "${userInput}" -> "${lowerInput}"`);
    
    // Check for wallet generation with number (including "1 wallet")
    console.log(`[AgentChat] Testing wallet generation patterns...`);
    const pattern1 = lowerInput.match(/(?:generate|create|make)\s+(\d+)\s+wallets?/i);
    const pattern2 = lowerInput.match(/(?:generate|create|make)\s+wallets?\s+(\d+)/i);
    const pattern3 = lowerInput.match(/(\d+)\s+wallets?\s+(?:for me|please)?/i);
    const pattern4 = lowerInput.match(/(?:make|create|generate)\s+(\d+)\s+wallets?\s+(?:for me|please)/i);
    
    console.log(`[AgentChat] Pattern matches:`, { pattern1, pattern2, pattern3, pattern4 });
    
    const multiWalletMatch = pattern1 || pattern2 || pattern3 || pattern4;
    
    if (multiWalletMatch) {
      const count = parseInt(multiWalletMatch[1] || multiWalletMatch[2]);
      console.log(`[AgentChat] Detected wallet generation request for ${count} wallet(s)`);
      
      // Use single wallet generation for 1, bulk for multiple
      try {
        if (count === 1) {
          console.log(`[AgentChat] Calling handleGenerateWallet()`);
          await handleGenerateWallet();
          console.log(`[AgentChat] handleGenerateWallet() completed`);
        } else {
          console.log(`[AgentChat] Calling handleGenerateMultipleWallets(${count})`);
          await handleGenerateMultipleWallets(count);
          console.log(`[AgentChat] handleGenerateMultipleWallets(${count}) completed`);
        }
        
        // Ensure loading is turned off after wallet generation
        setLoading(false);
        console.log(`[AgentChat] Wallet generation completed, loading set to false`);
      } catch (walletError) {
        console.error(`[AgentChat] Error generating wallets:`, walletError);
        const errorMessage = {
          id: generateUniqueId(),
          role: 'system',
          content: `Failed to generate wallets: ${walletError.message}`,
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
        setLoading(false);
      }
      return;
    }
    
    // More comprehensive wallet display patterns
    const walletShowPatterns = [
      lowerInput.includes('show wallet'),
      lowerInput.includes('view wallet'), 
      lowerInput.includes('list wallet'),
      lowerInput.includes('my wallet'),
      lowerInput.includes('show my wallet'),
      lowerInput.includes('view my wallet'),
      !!lowerInput.match(/show\s+.*wallet/i),
      !!lowerInput.match(/view\s+.*wallet/i),
      !!lowerInput.match(/list\s+.*wallet/i),
      !!lowerInput.match(/my\s+wallet/i)
    ];
    
    console.log(`[AgentChat] Wallet show pattern matches:`, walletShowPatterns);
    
    if (walletShowPatterns.some(match => match)) {
      await handleDisplayWallets();
      setLoading(false);
      return;
    }
    
    if (lowerInput.includes('export wallet') || lowerInput.includes('download wallet') ||
        lowerInput.includes('excel') || lowerInput.includes('spreadsheet')) {
      await handleExportWallets();
      setLoading(false);
      return;
    }
    
    // Single wallet generation patterns (without numbers)
    if (lowerInput.includes('generate wallet') || lowerInput.includes('create wallet') ||
        lowerInput.includes('new wallet') || lowerInput.includes('make wallet') ||
        lowerInput.match(/generate\s+a\s+wallet/i) || lowerInput.match(/create\s+a\s+wallet/i) ||
        lowerInput.match(/make\s+a\s+wallet/i) || lowerInput.match(/give\s+me\s+a?\s*wallet/i)) {
      console.log(`[AgentChat] Single wallet generation pattern matched`);
      try {
        await handleGenerateWallet();
        console.log(`[AgentChat] Single wallet generation completed`);
      } catch (error) {
        console.error(`[AgentChat] Single wallet generation error:`, error);
      }
      setLoading(false);
      return;
    }
    
    // Check agent status FIRST - if running and message has strategy keywords, use modify_preference ONLY
    try {
      const agentStatus = await tradingAgentApi.getAgentStatus(agent.id);
      console.log(`[AgentChat] Agent status check:`, agentStatus);

      if (agentStatus.status === 'running' || agentStatus.status === 'paused') {
        // Check if the message contains strategy-related keywords
        const lowerInput = userInput.toLowerCase();
        const isStrategyRelated =
          lowerInput.includes('trade') ||
          lowerInput.includes('buy') ||
          lowerInput.includes('sell') ||
          lowerInput.includes('stop') ||
          lowerInput.includes('pause') ||
          lowerInput.includes('resume') ||
          lowerInput.includes('don\'t') ||
          lowerInput.includes('do not') ||
          lowerInput.includes('change') ||
          lowerInput.includes('modify') ||
          lowerInput.includes('adjust') ||
          lowerInput.includes('increase') ||
          lowerInput.includes('decrease') ||
          lowerInput.includes('size') ||
          lowerInput.includes('amount') ||
          lowerInput.includes('frequency') ||
          lowerInput.includes('continue') ||
          lowerInput.includes('start') ||
          lowerInput.includes('begin');

        if (isStrategyRelated) {
          console.log(`[AgentChat] Agent is ${agentStatus.status} and message contains strategy keywords, using modify preference ONLY`);

          try {
            const modifyResult = await tradingAgentApi.modifyPreference(agent.id, userInput);
            console.log(`[AgentChat] Modify preference result:`, modifyResult);

            let responseContent = '';
            if (modifyResult.status === 'success') {
              const expiresAt = new Date(modifyResult.preference.expires_at_ts * 1000);
              responseContent = `✅ I've successfully updated your trading preferences while the strategy is running.

**Preference Update:**
${modifyResult.preference.summary}

This preference will be active until ${expiresAt.toLocaleString()} and will affect future trading decisions.

The agent will continue running with your updated preferences.`;
            } else {
              responseContent = `Unable to update preferences: ${modifyResult.message || 'Unknown error occurred'}`;
            }

            // Add system response
            const systemResponse = {
              id: generateUniqueId(),
              role: 'system',
              content: responseContent
            };

            setMessages(prev => {
              const updatedMessages = [...prev, systemResponse];
              saveLocalChat(agent.id, updatedMessages);
              return updatedMessages;
            });

            setLoading(false);
            return; // EXIT EARLY - don't call parseInstruction at all
          } catch (modifyError) {
            console.error(`[AgentChat] Error modifying preference:`, modifyError);
            // If modify preference fails, fall through to normal handling
            console.log(`[AgentChat] Modify preference failed, falling back to normal classification handling`);
          }
        }
      }
    } catch (statusError) {
      console.log(`[AgentChat] Could not get agent status:`, statusError);
    }

    // ONLY parse instruction if we didn't handle it as a preference modification
    console.log(`[AgentChat] No preference modification, proceeding with normal classification`);
    const classificationResult = await parseInstructionWithErrorHandling(
      agent.id,
      userInput,
      tokenAddress || undefined,
      walletAddresses && walletAddresses.length > 0 ? walletAddresses : undefined
    );

    // Use the classification result
    const result = classificationResult;

    if (result.success) {
      // Handle successful response
      console.log(`Parse instruction response for agent ${agent.id}:`, result.data);
      
      // Save the parsed strategy from the external API
      setParsedStrategy(result.data.parsedPlan);
      
      // Check if this is a regular question or a trading strategy
      const isQuestion = result.data.isQuestion === true;
      
      let responseContent;
      if (isQuestion) {
        // Handle regular questions differently
        responseContent = result.data.explanation || 'Processing your question...';
        // Set flag to wait for WebSocket answer
        setWaitingForAnswer(true);
      } else if (result.data.isProcessing) {
        responseContent = "Processing your request. Please wait...";
      } else if (!result.data.feasible) {
        responseContent = result.data.explanation || 'I cannot process this request. Please provide more details or check your parameters.';
      } else {
        responseContent = `I've analyzed your trading instruction and here's what I'll execute:
        
${formatPlanDetails(result.data.parsedPlan)}

${result.data.explanation ? `\n${result.data.explanation}\n` : ''}
Please click **Confirm** to execute this trading strategy, or type a new instruction to modify it.`;
      }
      
      // Add system response
      const systemResponse = {
        id: generateUniqueId(),
        role: 'system',
        content: responseContent,
        actions: result.data.isProcessing ? [] : (result.data.feasible ? ['confirm'] : [])
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, systemResponse];
        // Save to storage
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
      
      // Save system response
      await saveMessage('system', responseContent);
    } else {
      // Handle error response
      console.error(`Error parsing instruction for agent ${agent?.id}:`, result.error);
      
      let errorContent;
      
      // First check if we have explanation or message in the error data
      if (result.errorData && result.errorData.explanation) {
        console.log(`Using explanation from API response: ${result.errorData.explanation}`);
        errorContent = result.errorData.explanation;
      } else if (result.errorData && result.errorData.message && !result.errorData.message.includes('Error')) {
        console.log(`Using message from API response: ${result.errorData.message}`);
        errorContent = result.errorData.message;
      }
      // Fall back to standard error messages for common errors
      else if (result.error && result.error.message && result.error.message.includes("Received static fallback data")) {
        errorContent = "Unable to connect to the trading AI. The service is experiencing issues with market data. Please try again later.";
      } else if (result.error && result.error.message && result.error.message.includes("429")) {
        errorContent = "The trading service is experiencing high traffic. Please wait a moment and try again.";
      } else if (result.error && result.error.response && result.error.response.status === 500) {
        errorContent = "The trading service encountered an internal error. Our team has been notified. Please try again later.";
      } else if (result.error && result.error.message && result.error.message.includes("fetch")) {
        errorContent = "Unable to fetch market data for your trading instruction. Please check the token address and try again later.";
      } else {
        errorContent = result.errorMessage || 
                      'Unable to connect to trading agent after multiple attempts. Please try again later.';
      }
      
      // Handle error
      const errorMessage = {
        id: generateUniqueId(),
        role: 'system',
        content: errorContent,
        isError: true
      };

      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        // Save to storage
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
      
      // Save error message
      await saveMessage('system', errorContent);
    }
  } catch (error) {
    console.error('Error processing message:', error);
    
    // Add error message
    const errorMessage = {
      id: generateUniqueId(),
      role: 'system',
      content: `Sorry, I encountered an error: ${error.message}`,
      isError: true
    };
    
    setMessages(prev => {
      const updatedMessages = [...prev, errorMessage];
      saveLocalChat(agent.id, updatedMessages);
      return updatedMessages;
    });
  } finally {
    setLoading(false);
  }
};
// Generate a general response for non-trading messages
  const generateGeneralResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    // Check for common commands or questions
    if (input.includes('hello') || input.includes('hi ') || input.includes('hey')) {
      return `Hello! I'm your AI trading assistant. How can I help you today? You can ask me to:
- Execute trading strategies
- Generate new Solana wallets
- View your existing wallets
- Export wallet information`;
    }
    
    if (input.includes('help') || input.includes('what can you do')) {
      return `I can help you with the following:
      
**Trading:**
- Execute trading strategies on tokens
- Analyze market conditions
- Place orders and monitor execution

**Wallet Management:**
- Generate new Solana wallets
- View all your existing wallets
- Export wallet information to Excel
- Securely store wallet information

To get started with trading, provide a token address in the settings below, then tell me what you'd like to trade.`;
    }
    
    if (input.includes('show option') || input.includes('optional setting') || input.includes('setting')) {
      // Toggle optional settings
      setTimeout(() => setShowOptionalFields(true), 100);
      
      return `I've opened the settings panel where you can enter:
- Token Address: The contract address of the token you want to trade
- Wallet Address: Your wallet address for executing trades

These are optional for general chat but required for trading operations.`;
    }
    
    // Default general response
    return `I understand you're asking about "${userInput}". 

I'm primarily designed to help with trading operations and wallet management. If you'd like to:

- Execute a trading strategy, please provide a token address in the settings below
- Generate a new wallet, just ask me to "generate a new wallet"
- Generate multiple wallets, try "generate 5 wallets" (up to 10)
- View your existing wallets, ask to "show my wallets"

Is there something specific you'd like help with?`;
  };
  
  // UPDATED: formatPlanDetails function that only shows what the API returns
  const formatPlanDetails = (plan) => {
    if (!plan) return '';
    
    // If the plan is empty or only has internal fields, return an error message
    const validKeys = Object.keys(plan).filter(key => 
      !['strategy_type', 'type', 'external_id', 'id'].includes(key)
    );
    
    if (validKeys.length === 0) {
      return 'Unable to come up with trading strategy, please retry.';
    }
    
    // Extract all properties from the plan object
    let details = '';
    for (const [key, value] of Object.entries(plan)) {
      // Skip internal, redundant, or null-value fields
      if (['strategy_type', 'type', 'external_id', 'id'].includes(key)) continue;
      // if (key === 'price_upper_bound' || key === 'price_lower_bound') continue;
      if (value === null || value === undefined) continue;
      
      // Format the key for display
      const formattedKey = key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      
      // Format value based on type
      let formattedValue = value;
      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          formattedValue = value.join(", ");
        } else {
          formattedValue = JSON.stringify(value, null, 2);
        }
      }
      
      details += `**${formattedKey}:** ${formattedValue}\n`;
    }
    
    // Add strategy type at the beginning if available
    if (plan.strategy_type || plan.type) {
      const strategyType = plan.strategy_type || plan.type;
      details = `**Strategy Type:** ${strategyType}\n` + details;
    }
    
    return details;
  };
  
  const handleConfirm = async () => {
    if (!parsedStrategy) return;
    
    // Add confirmation message
    const confirmMessage = {
      id: Date.now(),
      role: 'system',
      content: "Strategy confirmed! Deploying trading agent..."
    };
    
    setMessages(prev => {
      const updatedMessages = [...prev, confirmMessage];
      // Save to storage
      saveLocalChat(agent.id, updatedMessages);
      return updatedMessages;
    });
    
    // Save confirmation message
    await saveMessage('system', "Strategy confirmed! Deploying trading agent...");
    
    try {
      // Save current strategy details for persistence
      const strategyDetails = {
        parsedStrategy,
        tokenAddress,
        walletAddresses
      };
      
      // Store in localStorage for resilience
      localStorage.setItem(`agent_strategy_${agent.id}`, JSON.stringify(strategyDetails));
      
      // Start strategy with the external service
      const result = await onStrategyStart({
        ...parsedStrategy,
        token_address: tokenAddress,
        wallet_addresses: walletAddresses
      });
      
      // Add success message with any additional info from the start operation
      const successMessage = {
        id: Date.now() + 1,
        role: 'system',
        content: result.message || "The trading agent has been successfully deployed. You can view its activity in the terminal below."
      };
      
      // Also add a message about continued chat availability
      const continueChatMessage = {
        id: Date.now() + 2,
        role: 'system',
        content: "You can continue to chat with me while the strategy is running. If you want to adjust the strategy parameters, please first stop the current execution using the controls in the terminal panel."
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, successMessage, continueChatMessage];
        // Save to storage
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
      
      // Save success messages
      await saveMessage('system', result.message || "The trading agent has been successfully deployed. You can view its activity in the terminal below.");
      await saveMessage('system', continueChatMessage.content);
    } catch (error) {
      console.error(`Error starting strategy for agent ${agent?.id}:`, error);
      
      // Add error message
      const errorContent = `Failed to start the strategy: ${error.response?.data?.error || error.message}`;
      const errorMessage = {
        id: Date.now() + 1,
        role: 'system',
        content: errorContent,
        isError: true
      };
      
      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        // Save to storage
        saveLocalChat(agent.id, updatedMessages);
        return updatedMessages;
      });
      
      // Save error message
      await saveMessage('system', errorContent);
    }
    
    // Clear parsed strategy
    setParsedStrategy(null);
  };


// Handle file download for Excel files
const handleFileDownload = (fileData) => {
  if (!fileData) return;
  
  try {
    const filename = fileData.filename || 'download.xlsx';
    
    if (fileData.type === 'excel') {
      // Create proper Excel data structure
      // Create CSV content (simpler than full Excel but works)
      let csvContent = '';
      
      // Add each row to CSV with proper escaping
      fileData.data.forEach((row, rowIndex) => {
        const escapedRow = row.map(cell => {
          if (cell === null || cell === undefined) return '';
          
          let cellStr = String(cell);
          
          // If the cell contains comma, newline, or quote, wrap in quotes and escape internal quotes
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            cellStr = '"' + cellStr.replace(/"/g, '""') + '"';
          }
          
          return cellStr;
        });
        
        csvContent += escapedRow.join(',');
        if (rowIndex < fileData.data.length - 1) {
          csvContent += '\n';
        }
      });
      
      // Create blob with proper encoding for Excel
      const blob = new Blob(['\ufeff' + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      
      // Create download URL
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link element and trigger download
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", filename.replace('.xlsx', '.csv'));
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL object
      window.URL.revokeObjectURL(url);
      
      console.log(`Successfully downloaded ${filename}`);
    } else {
      // For other file types
      let content = '';
      if (Array.isArray(fileData.data)) {
        content = fileData.data.map(row => 
          Array.isArray(row) ? row.join(',') : String(row)
        ).join('\n');
      } else {
        content = String(fileData.data);
      }
      
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error creating file for download:', error);
    alert('Failed to download file. Please try again.');
  }
};
  
  // Toggle optional settings
  const toggleOptionalFields = () => {
    setShowOptionalFields(!showOptionalFields);
  };
  
  // Helper to detect duplicate welcome messages
  const hasDuplicateWelcomeMessages = () => {
    let welcomeCount = 0;
    messages.forEach(msg => {
      if (msg.content.includes("Welcome to") && msg.role === "system") {
        welcomeCount++;
      }
    });
    return welcomeCount > 1;
  };
  
  // Helper to detect duplicate monitoring messages
  const hasDuplicateMonitoringMessages = () => {
    let monitorCount = 0;
    messages.forEach(msg => {
      if (msg.content.includes("I'm monitoring your active strategy") && msg.role === "system") {
        monitorCount++;
      }
    });
    return monitorCount > 1;
  };
  
  // Remove duplicate messages if detected
  useEffect(() => {
    if (!agent || !agent.id) return;
    
    // Don't run this if we don't have messages
    if (messages.length === 0) return;
    
    if (hasDuplicateWelcomeMessages() || hasDuplicateMonitoringMessages()) {
      console.log(`[DEBUG] Detected duplicate messages for agent ${agent.id}, cleaning up...`);
      let seenWelcomeMessage = false;
      let seenMonitoringMessage = false;
      const uniqueMessages = [];
      
      // Filter out duplicates
      for (const msg of messages) {
        const isWelcome = msg.content.includes("Welcome to") && msg.role === "system";
        const isMonitoring = msg.content.includes("I'm monitoring your active strategy") && msg.role === "system";
        
        if ((isWelcome && !seenWelcomeMessage) || (isMonitoring && !seenMonitoringMessage) || (!isWelcome && !isMonitoring)) {
          uniqueMessages.push(msg);
          if (isWelcome) seenWelcomeMessage = true;
          if (isMonitoring) seenMonitoringMessage = true;
        }
      }
      
      if (uniqueMessages.length !== messages.length) {
        console.log(`[DEBUG] Removed ${messages.length - uniqueMessages.length} duplicate messages for agent ${agent.id}`);
        
        // Update the messages
        setMessages(uniqueMessages);
        
        // Save the cleaned-up messages
        saveLocalChat(agent.id, uniqueMessages);
      }
    }
  }, [messages, agent?.id]);
  
  return (
    <div className="flex flex-col lg:h-full">
      {/* Chat messages area - Responsive */}
      <div className="flex-1 lg:overflow-y-auto mb-3 sm:mb-4 bg-white dark:bg-gray-800 rounded-lg shadow transition-colors min-h-[400px] lg:min-h-0">
        {initialLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            {messages.map(message => (
              <div
                key={message.id}
                className={`${message.role === 'user' ? 'ml-auto' : 'mr-auto'} max-w-[90%] sm:max-w-[85%] lg:max-w-[80%]`}
              >
                <div
                  className={`p-3 sm:p-4 rounded-2xl shadow-sm text-sm sm:text-base ${
                    message.role === 'user'
                      ? 'bg-purple-600 text-white rounded-tr-none'
                      : message.isError
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 rounded-tl-none'
                        : message.isWalletGeneration
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700 rounded-tl-none'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-tl-none'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{message.content}</div>
                  
                  {message.fileData && (
                    <div className="mt-3">
                      <button
                        onClick={() => handleFileDownload(message.fileData)}
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm font-medium flex items-center justify-center sm:justify-start space-x-2 text-sm sm:text-base"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span className="truncate">Download {message.fileData.filename}</span>
                      </button>
                    </div>
                  )}

                  {message.actions && message.actions.includes('confirm') && parsedStrategy && (
                    <div className="flex mt-4">
                      <button
                        onClick={handleConfirm}
                        className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm font-medium text-sm sm:text-base"
                      >
                        Execute Strategy
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start mb-3 sm:mb-4">
                <div className="bg-gray-100 dark:bg-gray-700 p-2.5 sm:p-3 rounded-2xl rounded-tl-none shadow-sm transition-colors">
                  <div className="flex space-x-1.5 sm:space-x-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messageEndRef}></div>
          </div>
        )}
      </div>

      {/* Optional Settings Toggle - Responsive */}
      <button
        onClick={toggleOptionalFields}
        className="mb-2 text-xs sm:text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 flex items-center space-x-1 self-start transition-colors p-1 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 sm:h-4 sm:w-4 transform transition-transform ${showOptionalFields ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span>{showOptionalFields ? 'Hide Settings' : 'Show Settings'}</span>
      </button>
      
      {/* Optional Settings */}
      {showOptionalFields && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 border border-gray-200 dark:border-gray-600 transition-colors">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Trading Settings</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Token Address
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  (Automatically set for current token)
                </span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="token_address"
                  value={tokenAddress}
                  readOnly
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-mono text-sm cursor-default"
                  placeholder="Token address will be automatically filled..."
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              {tokenAddress && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  ✓ Using mint address for current token
                </p>
              )}
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Wallet Address
                </label>
                <button
                  onClick={handleGenerateWallet}
                  disabled={generatingWallet}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 flex items-center space-x-1 transition-colors disabled:opacity-50"
                >
                  {generatingWallet ? 'Generating...' : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Generate New Wallet</span>
                    </>
                  )}
                </button>
              </div>
              {selectedGroup && (
  <div className="mt-2">
    <div className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">Selected Group</div>
    <div className="flex items-center p-2 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-600 rounded-md transition-colors">
      <div 
        className="w-3 h-3 rounded-full mr-2" 
        style={{ backgroundColor: selectedGroup.color || '#9333EA' }}
      ></div>
      <span className="font-medium">{selectedGroup.name}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
        ({groupWallets.length} wallets)
      </span>
      <button
        onClick={() => {
          setSelectedGroup(null);
          setGroupWallets([]);
          if (selectedWallet) {
            setWalletAddresses([selectedWallet.address]);
          } else {
            setWalletAddresses([]);
          }
        }}
        className="ml-auto text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
)}
              
              <WalletGroupSelector 
                selectedWallet={selectedWallet}
                selectedGroupProp={selectedGroup} // Note the renamed prop
                onSelectWallet={handleWalletSelect}
                onSelectGroup={handleGroupSelect}
              />
            </div>
              
              {/* Update wallet selection with WalletSelector component */}
              {wallets.length > 0 ? (
                <WalletSelector 
                  wallets={wallets}
                  selectedWallet={selectedWallet}
                  onSelect={handleWalletSelect}
                />
              ) : (
                <div className="flex">
                  <input
                    type="text"
                    value={walletAddresses.join(', ')}
                    onChange={(e) => {
                      const addresses = e.target.value.split(',').map(addr => addr.trim()).filter(Boolean);
                      setWalletAddresses(addresses);
                      
                      if (agent && agent.id) {
                        try {
                          const strategyDetailsKey = `agent_strategy_${agent.id}`;
                          const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
                          const parsedDetails = JSON.parse(storedDetails);
                          parsedDetails.walletAddresses = addresses;
                          localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
                        } catch (error) {
                          console.error('Error saving wallet addresses:', error);
                        }
                      }
                    }}
                    placeholder="Wallet address"
                    className="flex-1 p-2 sm:p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors text-xs sm:text-sm font-mono"
                    style={{fontSize: 'max(12px, 0.875rem)'}}
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Enter your wallet address or generate a new one
              </p>
            </div>
          </div>

      )}
      
      {/* Message input - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-2 sm:p-3 lg:p-4 transition-colors">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              console.log('[AgentChat] Key pressed:', e.key);
              if (e.key === 'Enter') {
                e.preventDefault();
                console.log('[AgentChat] Enter key detected, calling handleSendMessage');
                handleSendMessage();
              }
            }}
            placeholder={agent && agent.active
              ? "Ask about strategy..."
              : "Ask about trading..."}
            className="flex-1 p-2 sm:p-2.5 lg:p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
            style={{fontSize: 'max(16px, 0.875rem)'}}
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            disabled={loading}
            className="px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition-colors font-medium disabled:opacity-70 disabled:cursor-not-allowed text-xs sm:text-sm lg:text-base flex-shrink-0">
            {loading ? (
              <>
                <span className="hidden sm:inline">Sending...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              'Send'
            )}
          </button>
        </div>
        {agent && agent.active && (
          <p className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 mt-2">
            Strategy is active. You can continue to chat while it runs.
          </p>
        )}
        {usingLocalStorage && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Using offline storage for chat messages. They will sync when connection is restored.
          </p>
        )}
      </div>
    </div>
  );
};

export default AgentChat;